import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, takeUntil, lastValueFrom } from 'rxjs';
import CustomStore from 'devextreme/data/custom_store';

import { LocatorSummaryStatsService } from 'src/app/locator/services/locator-summary-stats.service';
import { LocatorSummaryStats } from 'src/app/locator/models/locator-summary-stats';
import * as locatorStore from 'src/app/locator-store';
import { shapeInSummaryStatsGridClicked } from 'src/app/locator-store/actions/locator-summary-stats-actions';
import { SummaryStatsDataGridService } from 'src/app/core/services/summary-stats-datagrid.service';
import { ConsoleLogService } from 'src/app/shared/services/console-log.service';

interface ColumnDefinition {
  dataField: string;
  caption: string;
  dataType?: string;
  format?: string;
  allowSorting: boolean;
  allowFiltering: boolean;
  allowHiding?: boolean;
  width?: number;
}

@Component({
  selector: 'atlas-summary-stats-grid',
  templateUrl: './summary-stats-grid.component.html',
  styleUrls: ['./summary-stats-grid.component.less'],
  standalone: false
})
export class SummaryStatsGridComponent implements OnInit, OnDestroy {
  dataSource: CustomStore;
  columns: ColumnDefinition[] = [];
  isLoading = true;
  currentLibraryId: number;
  loadingKey = 0; // Used to force recreation of loading component

  private destroy$ = new Subject<void>();

  constructor(
    private locatorSummaryStatsService: LocatorSummaryStatsService,
    private locatorStore$: Store<locatorStore.State>,
    private dataGridService: SummaryStatsDataGridService,
    private logService: ConsoleLogService
  ) { }

  ngOnInit(): void {
    // Register this component with the service so effects can trigger summary stats regeneration
    this.dataGridService.setStatsGridComponent(this);

    this.locatorStore$
      .select(locatorStore.getLocatorLibraryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((libraryId) => {
        if (libraryId) {
          this.currentLibraryId = libraryId;
          this.isLoading = true;
        }
      });
  }

  onInitialized(event: any): void {
    // Use the raw DevExtreme instance from the event
    // Set the grid reference early, but don't do selection yet (dataSource not ready)
    const gridInstance = event.component;
    if (gridInstance) {
      this.dataGridService.setDxDataGrid(gridInstance);
    }
  }

  onContentReady(event: any): void {
    const gridInstance = event.component;
    if (gridInstance && this.columns.length > 0) {
      // Check if there's any existing sort
      const columns = gridInstance.getVisibleColumns();
      const hasSortedColumn = columns.some((col: any) => col.sortOrder);

      // If no column is sorted, set default sort to location_name
      if (!hasSortedColumn) {
        const locationNameColumn = columns.find((col: any) => col.dataField === 'location_name');
        if (locationNameColumn) {
          gridInstance.columnOption('location_name', 'sortOrder', 'asc');
        }
      }
    }
  }

  onSelectionChanged(event: any): void {
    this.logService.log('Selected rows:', event.selectedRowsData);
    this.logService.log('Current selected row keys:', event.currentSelectedRowKeys);

    // get the shape_id for items in the selectedRowsData
    const shapeIds: number[] = event.selectedRowsData.map((row: any) => row.shape_id);

    if (shapeIds.length > 0) {
      this.locatorStore$.dispatch(
        shapeInSummaryStatsGridClicked({
          libraryDataId: event.selectedRowsData[0].location_id,
          shapeIds: shapeIds,
        })
      );
    }
  }

  customSaveState = (state: any): Promise<void> => {
    // Remove selection-related properties before saving
    const stateToSave = { ...state };
    delete stateToSave.selectedRowKeys;
    delete stateToSave.selectionFilter;

    localStorage.setItem('floatingOverlayPanelSummaryStatsGrid', JSON.stringify(stateToSave));
    return Promise.resolve();
  }

  customLoadState = (): Promise<any> => {
    const savedState = localStorage.getItem('floatingOverlayPanelSummaryStatsGrid');
    const state = savedState ? JSON.parse(savedState) : null;
    return Promise.resolve(state);
  }

  onLoadingComplete(): void {
    this.isLoading = false;
    this.initializeDataSource();
    this.loadColumnsMetadata();

    // Check if there's a pending shape to select
    const pendingShapeId = this.dataGridService.getPendingShapeIdToSelect();
    if (pendingShapeId !== null) {
      this.selectShapeInGrid(pendingShapeId);
      this.dataGridService.clearPendingShapeIdToSelect();
    }
  }

  onLoadingError(): void {
    this.isLoading = false;
    // Clear any pending selection on error
    this.dataGridService.clearPendingShapeIdToSelect();
  }

  triggerSummaryStatsRegeneration(): void {
    this.isLoading = true;
    this.loadingKey++;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private selectShapeInGrid(shapeId: number): void {
    // Wait for the data source to be initialized and the first data load to complete
    if (this.dataSource) {
      this.dataSource.load().then(() => {
        this.dataGridService.selectRow(shapeId);
      })
        .catch((error) => {
          this.logService.log('Error loading data before selection:', error);
        });
    }
  }

  private initializeDataSource(): void {
    this.dataSource = new CustomStore({
      key: 'shape_id',
      load: (loadOptions: any) => {
        return lastValueFrom(
          this.locatorSummaryStatsService.getLocatorLibrarySummaryStats(
            this.currentLibraryId,
            loadOptions
          )
        ).then((response) => {
          if (!response) {
            return { data: [], totalCount: 0 };
          }
          return {
            data: response.data,
            totalCount: response.totalCount
          };
        });
      }
    });
  }

  private loadColumnsMetadata(): void {
    this.locatorSummaryStatsService
      .getLocatorLibrarySummaryStats(this.currentLibraryId, {
        skip: 0,
        take: 1
      })
      .subscribe((response) => {
        if (response.data.length > 0) {
          this.generateColumns(response.data);
        }
      });
  }

  private generateColumns(sampleData: LocatorSummaryStats[]): void {
    if (sampleData.length === 0) {
      this.columns = [];
      return;
    }

    // Get all unique properties from the data source
    const allProperties = new Set<string>();
    sampleData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (typeof item[key] !== 'function') {
          allProperties.add(key);
        }
      });
    });

    // Define core columns in desired order
    const coreColumns = ['location_name', 'shape_name'];

    // Define columns to exclude from the grid
    const excludedColumns = ['location_id', 'library_id', 'shape_id'];

    // Remove excluded columns from the properties set
    excludedColumns.forEach((field) => allProperties.delete(field));

    // Generate column definitions
    this.columns = [];

    // Add core columns first
    coreColumns.forEach((field) => {
      if (allProperties.has(field)) {
        this.columns.push(this.createColumnDefinition(field, sampleData));
        allProperties.delete(field);
      }
    });

    // Add remaining dynamic columns
    Array.from(allProperties).forEach((field) => {
      this.columns.push(this.createColumnDefinition(field, sampleData));
    });
  }

  private createColumnDefinition(
    dataField: string,
    sampleData: LocatorSummaryStats[]
  ): ColumnDefinition {
    // Create a human-readable caption from the dataField
    const caption = this.formatFieldName(dataField);

    // Determine if this is a numeric field by checking the first non-null value
    const isNumeric = this.isNumericField(dataField, sampleData);

    // Set default width based on column type
    let width = 150; // default width for text columns
    if (dataField === 'location_name' || dataField === 'shape_name') {
      width = 200; // wider for key columns
    } else if (isNumeric) {
      width = 120; // narrower for numeric columns
    }

    const column: ColumnDefinition = {
      dataField,
      caption: dataField,
      allowSorting: true,
      allowFiltering: true,
      allowHiding: dataField !== 'location_name' && dataField !== 'shape_name',
      width: width
    };

    if (isNumeric) {
      column.dataType = 'number';
      column.format = '#,##0';
    }

    return column;
  }

  private formatFieldName(fieldName: string): string {
    // Convert snake_case to Title Case
    return fieldName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private isNumericField(
    fieldName: string,
    sampleData: LocatorSummaryStats[]
  ): boolean {
    // Check the first item in the sample data to determine if field is numeric
    if (sampleData.length === 0) return false;

    const firstItem = sampleData[0];
    const value = firstItem[fieldName];

    return typeof value === 'number';
  }


}